import { ReactNode, useEffect, useMemo, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import i18n from '../../../apps/legacy-app/src/utils/i18n';
import { Resource } from '../resources';

type ErrorListener = (error: string | ReactNode | null) => void;

class ApiMessagesStore {
  private resourceErrors: Map<Resource, string | ReactNode> = new Map();
  private listeners: Set<ErrorListener> = new Set();

  setError(resource: Resource, message: string | ReactNode, translate: boolean = true) {
    const translatedMessage = typeof message === 'string' && translate ? i18n.t(message) : message;

    this.clearError(resource);
    this.resourceErrors.set(resource, translatedMessage);

    enqueueSnackbar(translatedMessage, { variant: 'error' });

    this.listeners.forEach((listener) => listener(translatedMessage));
  }

  /** Reports an error without attaching it to a resource, so no screen watching the resource turns into an error page. */
  showError(message: string | ReactNode, translate: boolean = true) {
    enqueueSnackbar(typeof message === 'string' && translate ? i18n.t(message) : message, { variant: 'error' });
  }

  setSuccess(message: string, translate: boolean = true) {
    const translatedMessage: string = translate ? i18n.t(message) : message;
    enqueueSnackbar(translatedMessage, { variant: 'success' });
  }

  getErrorByResource(resource: Resource): string | ReactNode | null {
    return this.resourceErrors.get(resource) || null;
  }

  subscribe(listener: ErrorListener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
      return;
    };
  }

  clearError(resource: Resource) {
    this.resourceErrors.delete(resource);

    // Notify listeners that error was cleared
    this.listeners.forEach((listener) => listener(null));
  }

  clearAllErrors() {
    this.resourceErrors.clear();
    this.listeners.forEach((listener) => listener(null));
  }
}

export const apiMessagesStore = new ApiMessagesStore();

/**
 * React hook to track error state for one or more resources
 * @param resourceError - Single resource, array of resources, or undefined
 * @param clearErrorOnExit - Whether to clear errors when component unmounts (default: false)
 * @returns Array of error messages for the specified resources
 *
 * @example
 * // Single resource
 * const errors = useResourceError(resources.updateWorkplace);
 *
 * @example
 * // Multiple resources
 * const errors = useResourceError([resources.createWorkplace, resources.queryWorkplace]);
 *
 * @example
 * // With auto-clear on unmount
 * const errors = useResourceError(resources.save, true);
 */
export function useResourceError(
  resourceError?: Resource | Resource[],
  clearErrorOnExit: boolean = false
): (string | ReactNode)[] {
  const [errors, setErrors] = useState<(string | ReactNode)[]>([]);

  // Normalize to array
  const resources = useMemo(
    () => (resourceError ? (Array.isArray(resourceError) ? resourceError : [resourceError]) : []),
    [resourceError]
  );

  useEffect(() => {
    if (resources.length === 0) {
      setErrors([]);
      return;
    }

    const updateErrors = () => {
      const allErrors = resources
        .map((res) => apiMessagesStore.getErrorByResource(res))
        .filter((err): err is string | ReactNode => err !== null && err !== undefined);
      setErrors(allErrors);
    };

    const unsubscribe = apiMessagesStore.subscribe(() => {
      updateErrors();
    });

    // Set initial error state
    updateErrors();

    return () => {
      unsubscribe();
      // Optionally clear errors on unmount
      if (clearErrorOnExit) {
        resources.forEach((res) => apiMessagesStore.clearError(res));
      }
    };
  }, [resources, clearErrorOnExit]);

  return errors;
}
